import { ASC, DESC } from '@/components/shells/table/sort-directions';
import maxHeightMixin from '@/mixins/max-height-mixin';
import visibilityBasedOnFeatureSwitchMixin from '@/mixins/visibility-based-on-feature-switch-mixin';
import { cloneDeep } from 'lodash';

/**
 * @mixin
 */
export default {
	mixins: [maxHeightMixin, visibilityBasedOnFeatureSwitchMixin],
	data() {
		return {
			sortColumnIndex: null,
			sortDirection: null,
			ascending: ASC,
			descending: DESC,
			alignTablet: 'left',
			alignDesktop: 'left',
			headerHeight: 0
		};
	},
	props: {
		/**
		 * Array of columns in the recordset to display
		 * Format: [
				{
					columnName: 'Column1', //prop name in recordset
					columnDisplayName: 'Column 1 Name',
					display: true/false,
					isSortable: true/false,
					slot: 'Name of slot if overriding default display' //optional
					sortDirection: 'ascending'/'descending' //optional,
					alignTablet: 'center'/ 'left'/ 'right' // default left,
					alignDesktop: 'center' / 'left'/ 'right // default left'
				}
			]
		 */
		columns: {
			type: Array,
			default: () => [],
			validator: items => {
				const validItems = items.filter(
					item =>
						Object.prototype.hasOwnProperty.call(
							item,
							'columnName'
						) &&
						Object.prototype.hasOwnProperty.call(
							item,
							'columnDisplayName'
						) &&
						Object.prototype.hasOwnProperty.call(item, 'display')
				);

				return validItems.length === items.length;
			}
		},
		recordset: {
			type: Array,
			default: () => []
		},
		minHeight: {
			type: Number,
			default: 66
		},
		hasTableHeader: {
			type: Boolean,
			default: true
		},
		idColumn: {
			type: String,
			default: 'id'
		},
		noResultMessageEnabled: {
			type: Boolean,
			default: false
		},
		noResultsPrimaryText: {
			type: String,
			default: 'No Data To Display'
		},
		noResultsSecondaryText: {
			type: String,
			default: 'There is no data to display.'
		}
	},
	watch: {
		columns: {
			immediate: false,
			handler() {
				this.setCurrentSort();
			}
		}
	},
	mounted() {
		/* Calculate the height of the header and displace the scrollbar */
		if (this.$refs.tableHeader) {
			this.headerHeight = this.$refs.tableHeader.offsetHeight;
		} else {
			this.headerHeight = 0;
		}
	},
	created() {
		this.setCurrentSort();
	},
	computed: {
		/**
		 * Returns a recordset with only the columns that we are displaying
		 */
		displayRecordset() {
			let i = 1;
			const newRecordset = [];

			this.recordset.forEach(record => {
				const id = record[this.idColumn] ? record[this.idColumn] : i;
				const newRecord = {
					$id: id, //internal row key for sorting NOTE: unless an id column is provided, expanded will be lost after a sort
					childRecordset: record.childRecordset
				};
				for (const prop in record) {
					if (
						this.columns.find(
							column =>
								column.columnName == prop && column.display
						)
					) {
						newRecord[prop] = record[prop];
					}
				}

				newRecordset.push(newRecord);

				i += 1;
			});

			return newRecordset;
		},
		/**
		 *
		 * @returns {Array} - Recordset with the internal $id key
		 */
		internalRecordset() {
			let i = 1;
			const newRecordset = [];

			this.recordset.forEach(record => {
				const id = record[this.idColumn] ? record[this.idColumn] : i;
				const newRecord = cloneDeep(record);

				newRecord.$id = id;

				newRecordset.push(newRecord);

				i += 1;
			});

			return newRecordset;
		},
		displayColumns() {
			const displayCols = [
				...this.columns.filter(col => {
					if (!col.display) {
						return false;
					}

					if (col.featureSwitchId) {
						return this.isFeatureSwitchActive(col.featureSwitchId);
					}

					return true;
				})
			];

			if (this.sortColumnIndex != null)
				for (let index = 0; index < displayCols.length; index++) {
					if (
						index === this.sortColumnIndex &&
						displayCols[index].isSortable
					) {
						displayCols[index].sortDirection = this.sortDirection;
						displayCols[
							index
						].sortDirectionClass = this.sortDirectionClass(
							this.sortDirection
						);
						displayCols[
							index
						].sortDirectionIconClass = this.sortDirectionIconClass(
							this.sortDirection
						);
					} else {
						displayCols[index].sortDirection = null;
						displayCols[
							index
						].sortDirectionClass = this.sortDirectionClass();
						displayCols[
							index
						].sortDirectionIconClass = this.sortDirectionIconClass();
					}
				}
			return displayCols;
		},
		hasResults() {
			return this.recordset.length;
		},
		showNoResults() {
			return Boolean(
				!this.hasResults &&
					(this.$slots.noResults || this.noResultMessageEnabled)
			);
		}
	},
	methods: {
		setCurrentSort() {
			const displayCols = [...this.columns.filter(col => col.display)];

			if (displayCols) {
				const colIndex = displayCols.findIndex(c => {
					let result = false;

					if (
						Object.prototype.hasOwnProperty.call(c, 'sortDirection')
					) {
						if (
							c.sortDirection &&
							(c.sortDirection.toLowerCase() === this.ascending ||
								c.sortDirection.toLowerCase() ===
									this.descending)
						) {
							result = true;
						}
					}

					return result;
				});

				if (colIndex >= 0) {
					this.sortColumnIndex = colIndex;
					this.sortDirection = displayCols[
						colIndex
					].sortDirection.toLowerCase();
				}
			}
		},
		sortDirectionClass(sortDirection) {
			if (sortDirection) {
				sortDirection = sortDirection.toLowerCase();
			}

			switch (sortDirection) {
				case this.ascending:
					return 'is-ascending';
				case this.descending:
					return 'is-descending';
				default:
					return '';
			}
		},
		sortDirectionIconClass(sortDirection) {
			if (sortDirection) {
				sortDirection = sortDirection.toLowerCase();
			}

			switch (sortDirection) {
				case this.ascending:
					return 'icon-sort-direction';
				case this.descending:
					return 'icon-sort-direction';
				default:
					return 'icon-sort';
			}
		},
		sortDirectionClasses(sortDirection) {
			return `${this.sortDirectionClass(
				sortDirection
			)} ${this.sortDirectionIconClass(sortDirection)}`;
		},
		handleSort(
			sortDirection,
			columnName,
			columnIndex,
			currentSortDirection,
			isSortable
		) {
			if (isSortable && this.hasResults) {
				if (currentSortDirection) {
					if (currentSortDirection === this.ascending) {
						this.sortDirection = this.descending;
					} else {
						this.sortDirection = this.ascending;
					}
				} else {
					this.sortDirection = sortDirection;
				}

				this.sortColumnIndex = columnIndex;

				this.$emit('sort', {
					sortDirection: this.sortDirection,
					columnName: columnName
				});
			}
		},
		dynamicComponentEventHandler(event, data) {
			this.$emit(event, data);
		}
	}
};
